import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { RefundModel } from 'api-hooks/refund/refund';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import { HeaderCheckbox } from 'legacy-modules/sales/reschedule/header-checkbox';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

interface Props {
  parentIndex: number;
  type: string;
  refund?: RefundModel;
}

function reducer(state, action, { update, clearErrors, name, setValue }) {
  switch (action.type) {
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
            };

            // update(action.rowIndex, tempValue);
            setValue(`${name}.${action.rowIndex}`, tempValue);
            clearErrors(`${name}.${action.rowIndex}.${action.columnId}`);
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    case 'isSelected':
      return {
        ...state,
        data: state.data.map((row, index) => {
          const tempValue = {
            ...state.data[index],
            isSelected: action.value,
          };
          clearErrors(name);
          // update(index, tempValue);
          setValue(`${name}.${index}`, tempValue, { shouldValidate: true });

          return tempValue;
        }),
      };
    default:
      return state;
  }
}

export const _columns: (props: {
  t: any;
  editable: boolean;
  type: string;
  name: string;
  dispatch;
}) => IColumn<any>[] = ({ t, editable, type, dispatch, name }) => [
  {
    header: () => <HeaderCheckbox name={name} dispatch={dispatch} />,
    accessorKey: 'isSelected',
    minSize: 50,
    size: 50,
    checkboxNoLabel: true,
    dataType: ColumnDataType.checkbox,
  },
  {
    header: t('common:guest'),
    accessorKey: 'guestName',
    minSize: 150,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  ...(type === DocumentTypeEnum.visa
    ? [
        {
          header: t('common:visa'),
          accessorKey: 'preferenceName',
          minSize: 250,
          dataType: ColumnDataType.text,
          disabled: true,
        },
        {
          header: t('document:biometric'),
          accessorKey: 'biometric',
          minSize: 70,
          maxSize: 70,
          dataType: ColumnDataType.checkbox,
          disabled: true,
        },
        {
          header: t('document:appointment_date'),
          accessorKey: 'appointmentAt',
          minSize: 150,
          maxSize: 150,
          dataType: ColumnDataType.date,
          disabled: true,
        },
        {
          header: t('document:appointment_time'),
          accessorKey: 'appointmentAt',
          minSize: 120,
          maxSize: 120,
          dataType: ColumnDataType.time,
          disabled: true,
        },
      ]
    : type === DocumentTypeEnum.insurance
    ? [
        {
          header: t('common:type'),
          accessorKey: 'type',
          minSize: 100,
          maxSize: 100,
          dataType: ColumnDataType.text,
          disabled: true,
        },
        {
          header: t('common:started_at'),
          accessorKey: 'startAt',
          minSize: 110,
          maxSize: 110,
          dataType: ColumnDataType.date,
          disabled: true,
        },
        {
          header: t('common:ended_at'),
          accessorKey: 'endAt',
          minSize: 110,
          maxSize: 110,
          dataType: ColumnDataType.date,
          disabled: true,
        },
        {
          header: t('common:days'),
          accessorKey: 'days',
          minSize: 70,
          maxSize: 70,
          dataType: ColumnDataType.number,
          minNumber: 0,
          disabled: true,
        },
        {
          header: t('common:package'),
          accessorKey: 'package',
          minSize: 150,
          maxSize: 150,
          dataType: ColumnDataType.text,
          disabled: true,
        },
        {
          header: t('common:company'),
          accessorKey: 'company',
          minSize: 150,
          maxSize: 150,
          dataType: ColumnDataType.text,
          disabled: true,
        },
        {
          header: t('common:cover_area'),
          accessorKey: 'coverArea',
          minSize: 150,
          maxSize: 150,
          dataType: ColumnDataType.text,
          disabled: true,
        },
      ]
    : [
        {
          header: t('common:type'),
          accessorKey: 'type',
          minSize: 150,
          maxSize: 150,
          dataType: ColumnDataType.text,
          disabled: true,
        },
      ]),
  {
    header: t('common:description'),
    accessorKey: 'description',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.text,
    disabled: true,
  },
  {
    header: t('common:price'),
    accessorKey: 'price',
    minSize: 150,
    maxSize: 150,
    dataType: ColumnDataType.number,
    minNumber: 0,
    disabled: true,
  },
  {
    header: t('common:refund_price'),
    accessorKey: 'refundPrice',
    minSize: 100,
    dataType: ColumnDataType.number,
    minNumber: 0,
  },
];

export default function GuestDetailTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const [isInitiate, setIsInitiate] = React.useState<boolean>(false);
  const { control, clearErrors, setValue } = useFormContext();

  const name: any = `documentSales.${props.parentIndex}.documentSaleGuests`;

  const { fields, update } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const columns = (dispatch?: any) =>
    _columns({ t, editable, type: props.type, dispatch, name });

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, {
        update,
        clearErrors,
        name,
        setValue,
      }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate(true);
    }
  }, [fields, isInitiate]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      <Separator gap={24} />

      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns(dispatch)}
          data={state.data}
          dispatch={dispatch}
          noAddRemoveRow
          name={name}
          disabled={!editable || !!props.refund}
          uniqueRowKey="customId"
          onCustomDisabledCell={({ accessorKey, rowOriginal }) =>
            !!(accessorKey !== 'isSelected' && !rowOriginal.isSelected)
          }
        />
      </div>
    </>
  );
}
